import React, { createContext, useCallback, useContext, useState } from 'react';
import { useListener } from 'react-bus';
import { LoginFormInputs } from './Views/EmailLogin';
import { toast } from 'react-toastify';

export interface AuthUser {
  id: string | number;
  email: string;
  name: string;
  avatar: string | null;
  metadata: {
    [val: string]: any;
  };
}

export interface AuthenticationServiceType {
  getUser: () => Promise<AuthUser | null>;
  emailLogin: (
    email: string,
    password: string,
    remember?: boolean
  ) => Promise<void>;
  magicLinkLogin: (email: string) => Promise<void>;
  getRemembered: () => Promise<LoginFormInputs | undefined>;
  emailRegister: (
    name: string,
    email: string,
    password: string,
    password_confirmation: string
  ) => Promise<void>;
  socialLoginRegister: (provider: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (
    password: string,
    password_confirmation: string
  ) => Promise<void>;
}

export interface AuthenticationServiceConfig {
  socialProviders?: string[];
  termsUrl: string;
  supportUrl: string;
  logoUrl: string;
  logo: string;
  sidebarItems: {
    title: string;
    message: string;
  }[];
  registerTermsText?: any;
}

interface AuthContextProviderProps {
  registrationEnabled?: boolean;
  authenticationService: AuthenticationServiceType;
  authenticationServiceConfig: AuthenticationServiceConfig;
  children: React.ReactNode;
}

export interface AuthSession {
  user: AuthUser | null;
  actions: AuthenticationServiceType;
  config: AuthenticationServiceConfig;
  registrationEnabled: boolean;
  getUser: () => void;
  authReloading: boolean;
}

const UserContext = createContext<AuthSession>({
  user: null,
  // @ts-expect-error
  actions: {},
  // @ts-expect-error
  config: {},
  registrationEnabled: false,
  getUser: () => {},
  authReloading: false,
});

export const AuthContextProvider: React.FC<AuthContextProviderProps> = ({
  authenticationService,
  registrationEnabled = false,
  authenticationServiceConfig,
  children,
}) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useListener(
    'auth-state-changed',
    useCallback(() => {
      getUser();
    }, []) // eslint-disable-line
  );

  const getUser = async () => {
    setLoading(true);
    try {
      const data = await authenticationService.getUser();
      setUser(data);
    } catch (error) {
      setUser(null);
      toast('Failed to retrieve user data.', { type: 'error' });
    }
    setLoading(false);
  };

  const value = {
    user,
    actions: authenticationService,
    registrationEnabled: registrationEnabled,
    config: authenticationServiceConfig,
    getUser,
    authReloading: loading,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error(`useUser must be used within a UserContextProvider.`);
  }
  return context;
};
